<template>
  <v-container class="login-wrap" style="max-width: 480px; padding-top: 4rem">
    <v-card color="surface" class="pa-8">
      <div class="text-center mb-6">
        <v-icon color="primary" size="48" class="mb-2">mdi-dice-multiple</v-icon>
        <h1 class="text-h5 font-weight-bold">Welcome back</h1>
        <p class="text-body-2 text-grey">Log in to Backgammon Tracker</p>
      </div>

      <!-- OAuth Buttons -->
      <div class="mb-6">
        <v-btn
          block
          size="large"
          variant="outlined"
          class="mb-3"
          :loading="googleLoading"
          @click="handleGoogleLogin"
        >
          <v-icon start>mdi-google</v-icon>
          Continue with Google
        </v-btn>
        <v-btn
          block
          size="large"
          variant="outlined"
          color="white"
          :loading="appleLoading"
          @click="handleAppleLogin"
        >
          <v-icon start>mdi-apple</v-icon>
          Continue with Apple
        </v-btn>
      </div>

      <div class="d-flex align-center mb-6">
        <v-divider />
        <span class="mx-4 text-caption text-grey">or with email</span>
        <v-divider />
      </div>

      <!-- Email Login Form -->
      <v-form @submit.prevent="handleLogin">
        <v-text-field
          v-model="email"
          label="Email"
          type="email"
          prepend-inner-icon="mdi-email-outline"
          :rules="[(v) => !!v || 'Email is required']"
          class="mb-3"
        />
        <v-text-field
          v-model="password"
          label="Password"
          :type="showPw ? 'text' : 'password'"
          prepend-inner-icon="mdi-lock-outline"
          :append-inner-icon="showPw ? 'mdi-eye-off' : 'mdi-eye'"
          @click:append-inner="showPw = !showPw"
          :rules="[(v) => !!v || 'Password is required']"
          class="mb-1"
        />

        <div class="text-right mb-4">
          <router-link
            to="/forgot-password"
            class="text-caption text-primary text-decoration-none"
          >
            Forgot password?
          </router-link>
        </div>

        <v-btn
          type="submit"
          color="primary"
          size="large"
          block
          :loading="auth.loading"
        >
          Log ind
        </v-btn>
      </v-form>

      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        class="mt-4"
        density="compact"
      >
        {{ error }}
      </v-alert>

      <p class="text-center text-body-2 mt-6 text-grey">
        Har du ikke en konto?
        <router-link to="/register" class="text-primary text-decoration-none font-weight-bold">
          Opret konto
        </router-link>
      </p>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "../stores/auth";

const router = useRouter();
const route = useRoute();
const auth = useAuthStore();

const email = ref("");
const password = ref("");
const showPw = ref(false);
const error = ref("");
const googleLoading = ref(false);
const appleLoading = ref(false);

function redirect() {
  const dest = route.query.redirect || "/dashboard";
  router.push(dest);
}

async function handleLogin() {
  error.value = "";
  try {
    await auth.login({ email: email.value, password: password.value });
    redirect();
  } catch (e) {
    error.value = e.response?.data?.error || "Log ind fejlede";
  }
}

async function handleGoogleLogin() {
  googleLoading.value = true;
  error.value = "";
  try {
    const { google } = window;
    if (!google?.accounts?.id) {
      error.value = "Google Sign-In er ikke indlæst";
      googleLoading.value = false;
      return;
    }
    google.accounts.id.initialize({
      client_id: process.env.VUE_APP_GOOGLE_CLIENT_ID,
      callback: async (response) => {
        try {
          await auth.loginWithGoogle(response.credential);
          redirect();
        } catch (e) {
          error.value = e.response?.data?.error || "Google log ind fejlede";
        }
        googleLoading.value = false;
      },
    });
    google.accounts.id.prompt();
  } catch {
    error.value = "Google log ind fejlede";
    googleLoading.value = false;
  }
}

async function handleAppleLogin() {
  appleLoading.value = true;
  error.value = "";
  try {
    const { AppleID } = window;
    if (!AppleID?.auth) {
      error.value = "Apple Sign In er ikke indlæst";
      appleLoading.value = false;
      return;
    }
    const data = await AppleID.auth.signIn();
    await auth.loginWithApple(data.authorization.id_token, data.user);
    redirect();
  } catch (e) {
    if (e?.error !== "popup_closed_by_user") {
      error.value = e.response?.data?.error || "Apple log ind fejlede";
    }
  } finally {
    appleLoading.value = false;
  }
}
</script>
